import { colors } from '@/theme/tokens';

// Display-only helpers for Agent Studio → Costs. They format numbers the server
// computed; none of them computes a figure.

/** Money at a precision that keeps small run costs readable: $1,234.50 · $12.34 · $0.056 · $0.0049. */
export function usd(v: number | null | undefined): string {
  if (v == null) return '—';
  const a = Math.abs(v);
  if (a === 0) return '$0';
  if (a >= 1) return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (a >= 0.01) return '$' + v.toFixed(3);
  return '$' + v.toFixed(4);
}

/** Totals on the headline tiles: always cents. */
export function usdCents(v: number): string {
  return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function count(v: number): string {
  return v.toLocaleString('en-US');
}

/** 532,000,000 → "532 M". */
export function compact(v: number): string {
  if (v >= 1e9) return (v / 1e9).toFixed(1) + ' B';
  if (v >= 1e6) return Math.round(v / 1e6).toLocaleString('en-US') + ' M';
  if (v >= 1e3) return Math.round(v / 1e3).toLocaleString('en-US') + ' K';
  return String(v);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A "yyyy-MM-dd" UTC calendar day as text. Read off the string, never through new Date(),
 * which would show the previous day to every viewer west of UTC.
 */
export function dayLabel(day: string, withYear = false): string {
  const [y, m, d] = day.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]}${withYear ? ' ' + y : ''}`;
}

export function monthLabel(day: string): string {
  const [y, m] = day.split('-').map(Number);
  return `${MONTHS[m - 1]} ${y}`;
}

/** An instant from the API (UTC) as "7 Sep 03:03 UTC". */
export function instantUtc(iso: string): string {
  const t = new Date(iso);
  const hh = String(t.getUTCHours()).padStart(2, '0');
  const mm = String(t.getUTCMinutes()).padStart(2, '0');
  return `${t.getUTCDate()} ${MONTHS[t.getUTCMonth()]} ${hh}:${mm} UTC`;
}

const SERIES = [colors.amber[500], colors.ink[500], colors.violet[500], colors.green[500], colors.red[500], '#0EA5E9', '#EC4899', colors.slate[400]];

/** A model's colour, from its position in the report's spend order — stable within one report. */
export function modelColor(models: string[], model: string): string {
  const i = models.indexOf(model);
  return SERIES[(i < 0 ? models.length : i) % SERIES.length];
}
